import { useMutation, useQuery } from 'react-query';
import type { UseMutationOptions, UseQueryOptions } from 'react-query';

import type { ExpectedEarningsShare } from '@evenfinancial/finance-client';

import type { ExpectedEarningsShareRow } from '@portal/api-models';

import { client } from '../client';
import { QueryKey } from '../types';

const baseUrl = '/finance/expected-earnings-shares';

export type CreateExpectedEarningsShareData = {
  companyUuid: string;
  share: string;
  subAccountUuid: string;
};

export const getExpectedEarningsShares = async () => {
  return client
    .get<ExpectedEarningsShareRow[]>(baseUrl)
    .then(({ data }) => data);
};

export const getExpectedEarningsSharesBySubAccountUuid = async (
  uuid: string
) => {
  return client
    .get<ExpectedEarningsShare[]>(`${baseUrl}/subaccounts/${uuid}`)
    .then(({ data }) => data);
};

export interface ExpectedEarningsShareCreateData {
  companyUuid: string;
  share: number;
  subAccountUuid?: string;
}

export const createExpectedEarningsShare = async (
  data: ExpectedEarningsShareCreateData
) => {
  return client
    .post<ExpectedEarningsShare>(baseUrl, data)
    .then(({ data }) => data);
};

export const useGetExpectedEarningsSharesQuery = (
  options?: Omit<
    UseQueryOptions<ExpectedEarningsShareRow[]>,
    'queryKey' | 'queryFn'
  >
) =>
  useQuery(
    [QueryKey.ExpectedEarningsShares],
    async () => getExpectedEarningsShares(),
    options
  );

export const useGetExpectedEarningsSharesBySubAccountUuidQuery = (
  uuid: string,
  options?: Omit<
    UseQueryOptions<ExpectedEarningsShare[]>,
    'queryKey' | 'queryFn'
  >
) =>
  useQuery(
    [QueryKey.ExpectedEarningsShares, uuid],
    async () => getExpectedEarningsSharesBySubAccountUuid(uuid),
    options
  );

export const useCreateExpectedEarningsShareMutation = (
  options?: UseMutationOptions<
    ExpectedEarningsShare,
    unknown,
    ExpectedEarningsShareCreateData
  >
) => useMutation(createExpectedEarningsShare, options);
